import { sha1 } from 'js-sha1';
import { type SurfaceUnit } from '@pkgs/api/types';
import { type AutoSuggestionPropertyProfile, type Currency, MeasurementSystem, type ProfileV2 } from '@generated/search-bff';

import { getEntries } from './object';
import { DISPLAY_ID_PREFIXES } from '../constants/propertyData';

export const decodeHTML = (str: string): string => {
  const htmlEntities: Record<string, string> = {
    '&#34;': '"',
    '&amp;': '&',
    '&#39;': "'",
    '&gt;': '>',
    '&lt;': '<',
  };

  const entityRegex = new RegExp(`(${Object.keys(htmlEntities).join('|')})`, 'g');

  return str.replaceAll(entityRegex, (entity) => htmlEntities[entity]!);
};

export const formatShopPhoneNumber = (num: string): string => num.replaceAll('-', ' ');

export const createSessionToken = (): string => {
  return Math.random().toString(36).slice(2);
};

export const shortenMasterDataId = (uuid: string): string => {
  return sha1(uuid).slice(0, 8);
};

export const shortenMasterDataIds = (uuids: string[]): string[] =>
  uuids.map((shopId) => (isUUID(shopId) ? shortenMasterDataId(shopId) : shopId));

export const isNumeric = (value: string) => {
  return /^-?\d+$/.test(value);
};

export const hasDisplayIdPrefix = (id: string): boolean => {
  const regex = new RegExp(`^(${DISPLAY_ID_PREFIXES.join('|')})`);
  return regex.test(id.toUpperCase());
};

export const CURRENCY_SYMBOL_MAP: Record<Currency, string> = {
  EUR: '\u20AC',
  USD: '\u0024',
  CAD: 'CA' + '\u0024',
  GBP: '\u00A3',
  CHF: 'CHF',
  AED: 'AED',
  CLF: 'UF',
  CLP: 'CLP',
  CNY: 'CNY',
  COP: 'COP',
  CZK: 'CZK',
  DKK: 'DKK',
  HKD: 'HKD',
  HUF: 'Ft',
  OMR: 'OMR',
  MUR: 'MUR',
  QAR: 'QAR',
  SEK: 'SEK',
  TRY: 'TRY',
  ZAR: 'ZAR',
  BSD: 'BSD',
  BZD: 'BZD',
  CRC: 'CRC',
  KYD: 'KYD',
  MXN: 'MXN',
  PAB: 'PAB',
  PLN: 'PLN',
} as const;

export const UNIT_SYMBOL_MAP: Record<MeasurementSystem, string> = {
  [MeasurementSystem.metric]: 'm²',
  [MeasurementSystem.imperial]: 'sq ft',
} as const;

export const SURFACE_UNIT_MAP: Record<MeasurementSystem, SurfaceUnit> = {
  [MeasurementSystem.metric]: 'sqmt',
  [MeasurementSystem.imperial]: 'sqft',
} as const;

export const isUUID = (uuid: string): boolean => {
  return /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(uuid);
};

export function decodeProfileValues<T extends ProfileV2 | AutoSuggestionPropertyProfile>(profile: T): T {
  const decodedProfile: Partial<T> = {};
  for (const [key, value] of getEntries(profile)) {
    if (value && typeof value === 'string') {
      decodedProfile[key] = decodeHTML(value) as T[keyof T];
    } else {
      decodedProfile[key] = value;
    }
  }
  return decodedProfile as T;
}
